import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeXmark } from '@fortawesome/free-solid-svg-icons/faVolumeXmark';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { faFlagUsa } from '@fortawesome/free-solid-svg-icons/faFlagUsa';
import { faLeaf } from '@fortawesome/free-solid-svg-icons/faLeaf';
import { faCanadianMapleLeaf } from '@fortawesome/free-brands-svg-icons/faCanadianMapleLeaf';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { usePropertyCountry } from '../utils/propertyCountry';
import { useRevealStyles } from '../hooks/useRevealStyles';
import type { CSSProperties } from 'react';
import { useColor4 } from '../hooks/useColor4';
import { useReadableOnPrimary } from '../hooks/useReadableOnPrimary';

/**
 * "What Life Here Is Like For You" — 2×2 benefit grid for the
 * Better-Built Living page: Quieter Living, Greater Efficiency,
 * American-Made Excellence (Canadian-Made for properties in Canada), and
 * Sustainable by Design.
 *
 * Icons are intentionally baked in (not editable) — they are structural
 * illustrations of the benefit, not content.
 */

export function BetterBuiltBenefits() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });
  const color4 = useColor4();
  const readableOnPrimary = useReadableOnPrimary();
  const isCanadian = usePropertyCountry() === 'ca';
  const sectionVars = {
    ...readableOnPrimary,
    backgroundColor: 'var(--vibe-color-primary)',
    '--bbb-icon-bg': color4?.color ?? 'var(--vibe-color-secondary)',
    '--bbb-icon-fg': color4?.textOn ?? 'var(--vibe-color-text-on-secondary)',
  } as CSSProperties;

  return (
    <section
      className="w-full py-14 md:py-20 bg-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-primary)]"
      style={sectionVars}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef} className="reveal-fade-up text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <RichTextContent
            path="betterbuiltbenefits.title"
            label="Section Title"
            defaultValue='<p>What Life at <span data-type="field-token" data-field="property_name"></span> is like for You</p>'
            as="h2"
            className="font-heading text-xl sm:text-2xl md:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight tracking-tight"
          />
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10 max-w-5xl mx-auto">
          {/* Card 1 — Quieter Living */}
          <article
            data-reveal
            className="reveal-fade-up bbb-card bg-white border border-[var(--vibe-color-border)] p-7 sm:p-9 lg:p-10 flex flex-col"
          >
            <span className="bbb-icon mb-6 inline-flex items-center justify-center">
              <FontAwesomeIcon icon={faVolumeXmark} className="bbb-icon-glyph" aria-hidden="true" />
            </span>
            <RichTextContent
              path="betterbuiltbenefits.card1_title"
              defaultValue="<p>Quieter Living</p>"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 tracking-tight"
            />
            <RichTextContent
              path="betterbuiltbenefits.card1_body"
              defaultValue="<p>Experience enhanced sound insulation, thanks to steel framing and double-insulated walls that minimize noise and maximize comfort.</p>"
              as="div"
              className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-2 last:[&_p]:mb-0"
            />
          </article>

          {/* Card 2 — Greater Efficiency */}
          <article
            data-reveal
            className="reveal-fade-up bbb-card bg-white border border-[var(--vibe-color-border)] p-7 sm:p-9 lg:p-10 flex flex-col"
          >
            <span className="bbb-icon mb-6 inline-flex items-center justify-center">
              <FontAwesomeIcon icon={faBolt} className="bbb-icon-glyph" aria-hidden="true" />
            </span>
            <RichTextContent
              path="betterbuiltbenefits.card2_title"
              defaultValue="<p>Greater Efficiency</p>"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 tracking-tight"
            />
            <RichTextContent
              path="betterbuiltbenefits.card2_body"
              defaultValue="<p>Our off-site process delivers energy-efficient, durable homes with exceptional build quality that result in utility savings.</p>"
              as="div"
              className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-2 last:[&_p]:mb-0"
            />
          </article>

          {/* Card 3 — American-Made / Canadian-Made Excellence */}
          <article
            data-reveal
            className="reveal-fade-up bbb-card bg-white border border-[var(--vibe-color-border)] p-7 sm:p-9 lg:p-10 flex flex-col"
          >
            <span className="bbb-icon mb-6 inline-flex items-center justify-center">
              <FontAwesomeIcon
                icon={isCanadian ? faCanadianMapleLeaf : faFlagUsa}
                className="bbb-icon-glyph"
                aria-hidden="true"
              />
            </span>
            {isCanadian ? (
              <RichTextContent
                path="betterbuiltbenefits.card3_title_canada"
                defaultValue="<p>Canadian-Made Excellence</p>"
                as="h3"
                className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 tracking-tight"
              />
            ) : (
              <RichTextContent
                path="betterbuiltbenefits.card3_title"
                defaultValue="<p>American-Made Excellence</p>"
                as="h3"
                className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 tracking-tight"
              />
            )}
            <RichTextContent
              path="betterbuiltbenefits.card3_body"
              defaultValue="<p>We source high-quality, domestic materials for unmatched durability and craftsmanship in every home.</p>"
              as="div"
              className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-2 last:[&_p]:mb-0"
            />
          </article>

          {/* Card 4 — Sustainable by Design */}
          <article
            data-reveal
            className="reveal-fade-up bbb-card bg-white border border-[var(--vibe-color-border)] p-7 sm:p-9 lg:p-10 flex flex-col"
          >
            <span className="bbb-icon mb-6 inline-flex items-center justify-center">
              <FontAwesomeIcon icon={faLeaf} className="bbb-icon-glyph" aria-hidden="true" />
            </span>
            <RichTextContent
              path="betterbuiltbenefits.card4_title"
              defaultValue="<p>Sustainable by Design</p>"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 tracking-tight"
            />
            <RichTextContent
              path="betterbuiltbenefits.card4_body"
              defaultValue="<p>Our off-site process minimizes waste, ensuring eco-friendly construction with uncompromised quality and style.</p>"
              as="div"
              className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-2 last:[&_p]:mb-0"
            />
          </article>
        </div>
      </div>

      <style>{`
        .bbb-card {
          border-radius: 4px;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .bbb-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 34px -12px rgba(0, 0, 0, 0.35);
        }
        .bbb-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          color: var(--bbb-icon-fg);
          background: var(--bbb-icon-bg);
        }
        .bbb-icon-glyph {
          width: 26px;
          height: 26px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltBenefits',
  displayName: 'Better-Built Benefits',
  tokens: {
    'betterbuiltbenefits.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue:
        '<p>What Life at <span data-type="field-token" data-field="property_name"></span> is like for You</p>',
    },
    'betterbuiltbenefits.card1_title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Quieter Living</p>',
    },
    'betterbuiltbenefits.card1_body': {
      label: 'Card 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Experience enhanced sound insulation, thanks to steel framing and double-insulated walls that minimize noise and maximize comfort.</p>',
    },
    'betterbuiltbenefits.card2_title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Greater Efficiency</p>',
    },
    'betterbuiltbenefits.card2_body': {
      label: 'Card 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our off-site process delivers energy-efficient, durable homes with exceptional build quality that result in utility savings.</p>',
    },
    'betterbuiltbenefits.card3_title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>American-Made Excellence</p>',
    },
    'betterbuiltbenefits.card3_title_canada': {
      label: 'Card 3 Title (Canadian Properties)',
      type: 'richtext',
      defaultValue: '<p>Canadian-Made Excellence</p>',
    },
    'betterbuiltbenefits.card3_body': {
      label: 'Card 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>We source high-quality, domestic materials for unmatched durability and craftsmanship in every home.</p>',
    },
    'betterbuiltbenefits.card4_title': {
      label: 'Card 4 Title',
      type: 'richtext',
      defaultValue: '<p>Sustainable by Design</p>',
    },
    'betterbuiltbenefits.card4_body': {
      label: 'Card 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our off-site process minimizes waste, ensuring eco-friendly construction with uncompromised quality and style.</p>',
    },
  },
};

(BetterBuiltBenefits as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltBenefits;
